import type { Dispatch, SetStateAction } from "react";
import {
  ActionMenu,
  Button,
  StatusPill,
  type DataTableColumn,
} from "@playbookai/ui";
import * as api from "../../../api/admin-ai-profiles-api.js";
import type { LifecycleTarget } from "./LifecycleDialog.js";

/** The connections table's columns.
 *
 * A factory rather than a constant: every row action runs against state the
 * PAGE owns — which editor is open, which lifecycle change is pending, whether
 * a test is in flight — so the handlers are passed in and named exactly as the
 * page names them. */
export function connectionColumns({
  providers,
  setConnectionEditor,
  setProfileEditor,
  setInitialConnectionId,
  setLifecycle,
  controlsBusy,
  startTest,
  action,
}: {
  providers: { data: api.ProviderDescriptor[] | undefined };
  setConnectionEditor: Dispatch<
    SetStateAction<api.AdminConnection | "new" | null>
  >;
  setProfileEditor: Dispatch<
    SetStateAction<api.ExecutionProfile | "new" | null>
  >;
  setInitialConnectionId: Dispatch<SetStateAction<string | undefined>>;
  setLifecycle: Dispatch<SetStateAction<LifecycleTarget | null>>;
  controlsBusy: boolean;
  startTest: (connection: api.AdminConnection) => Promise<void>;
  action: (
    id: string,
    execute: () => Promise<unknown>,
    message: string,
  ) => Promise<void>;
}): DataTableColumn<api.AdminConnection>[] {
  return [
    {
      id: "name",
      /* Shares and floors (PB-5436): each floor is its share of a 625px table,
         the smallest that still gives Provider, Status and Actions the widths
         they had at a 1024px window. A narrower box (the Player AI rail at
         1024-1152px) scrolls inside the pinned region instead of clipping,
         and the pinned Connection column stays under half of it; at 38% it
         pinned 264px of a 444px box. */
      width: "30%",
      minWidth: 188,
      header: "Connection",
      cardRole: "title",
      cell: (row) => (
        <div className="flex flex-col leading-tight">
          {/* eslint-disable-next-line ds/no-hand-built -- kept: the entity-link look (weight 500, cyan, underline on hover only) has no Button option; Button variant="link" size="inline" underlines at rest and inherits the weight, a look change no PR 3 row approves. */}
          <button
            type="button"
            className="w-fit font-medium text-pb-accent-text hover:underline focus-ring"
            disabled={controlsBusy}
            onClick={() => setConnectionEditor(row)}
          >
            {row.display_name}
          </button>
          <span className="text-dense-1 text-pb-text-muted">
            <span>
              {row.profile_count}{" "}
              {row.profile_count === 1 ? "profile" : "profiles"} ·{" "}
              {row.locked ? "Locked" : "Editable"}
            </span>
            {" · "}
            <span>
              {row.provider_config?.allowed_transports === "both"
                ? "HTTP or WebSocket (set by deployment)"
                : row.provider_config?.allowed_transports === "websocket"
                  ? "WebSocket only"
                  : "HTTP only"}
            </span>
          </span>
        </div>
      ),
    },
    {
      id: "provider",
      width: "20%",
      minWidth: 125,
      header: "Provider",
      cell: (row) =>
        providers.data?.find((provider) => provider.id === row.provider_key)
          ?.label ?? row.provider_key,
    },
    {
      id: "status",
      width: "16%",
      minWidth: 100,
      header: "Status",
      cell: (row) => (
        <StatusPill tone={row.status === "active" ? "healthy" : "neutral"}>
          {row.status === "active" ? "Enabled" : "Disabled"}
        </StatusPill>
      ),
    },
    {
      id: "actions",
      width: "34%",
      minWidth: 213,
      header: "Actions",
      cell: (row) => (
        <div className="flex flex-nowrap items-center gap-2 whitespace-nowrap">
          <Button
            size="sm"
            variant="secondary"
            disabled={controlsBusy || row.mode !== "chat"}
            onClick={() => {
              setInitialConnectionId(row.id);
              setProfileEditor("new");
            }}
          >
            Create profile
          </Button>
          <ActionMenu
            triggerLabel={`Actions for connection ${row.display_name}`}
            triggerSize="sm"
            items={[
              {
                id: "edit",
                label: "Edit details",
                ...(controlsBusy
                  ? { disabledReason: "A change is in progress." }
                  : {}),
                onSelect: () => setConnectionEditor(row),
              },
              ...(row.mode === "embedding"
                ? [
                    {
                      id: "test",
                      label: "Test",
                      ...(controlsBusy
                        ? { disabledReason: "A change is in progress." }
                        : {}),
                      onSelect: () => void startTest(row),
                    },
                  ]
                : []),
              {
                id: "duplicate",
                label: row.locked ? "Create replacement" : "Duplicate",
                ...(controlsBusy
                  ? { disabledReason: "A change is in progress." }
                  : {}),
                onSelect: () =>
                  void action(
                    row.id,
                    async () => {
                      const copy = await api.duplicateConfiguration(
                        "connections",
                        row.id,
                      );
                      setConnectionEditor(copy as api.AdminConnection);
                    },
                    "Connection duplicated. Edit its configuration and create replacement profiles.",
                  ),
              },
              {
                id: "lifecycle",
                label: row.status === "active" ? "Disable" : "Enable",
                ...(controlsBusy
                  ? { disabledReason: "A change is in progress." }
                  : row.status !== "active" &&
                      !row.allowed_actions?.enable?.allowed
                    ? {
                        disabledReason:
                          row.allowed_actions?.enable?.reason ??
                          "This connection cannot be enabled.",
                      }
                    : {}),
                onSelect: () =>
                  row.status === "active"
                    ? setLifecycle({
                        kind: "connections",
                        row,
                        action: "disable",
                      })
                    : void action(
                        row.id,
                        () => api.changeLifecycle("connections", row, "enable"),
                        "Connection enabled.",
                      ),
              },
              {
                id: "delete",
                label: "Delete…",
                tone: "critical",
                separatorBefore: true,
                ...(controlsBusy
                  ? { disabledReason: "A change is in progress." }
                  : {}),
                onSelect: () =>
                  setLifecycle({ kind: "connections", row, action: "delete" }),
              },
            ]}
          />
        </div>
      ),
    },
  ];
}
